import React, { useState, useEffect } from 'react';
import {
  Palette, Plus, Search, Edit, Trash2, Upload, Download,
  FileText, Image as ImageIcon, Type, Eye, ExternalLink
} from 'lucide-react';
import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://content-review-api.ryantoth2410.workers.dev/api';

interface BrandAsset {
  id: number;
  client_id: number;
  client_name?: string;
  asset_type: string;
  asset_name: string;
  asset_url?: string;
  description?: string;
  created_at: string;
  file_size?: number;
  mime_type?: string;
}

interface BrandGuideline {
  client_id: number;
  client_name: string;
  logo_count: number;
  color_count: number;
  font_count: number;
  guideline_count: number;
  total_assets: number;
}

export default function BrandsManagement() {
  const [brandGuidelines, setBrandGuidelines] = useState<BrandGuideline[]>([]);
  const [assets, setAssets] = useState<BrandAsset[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClient, setSelectedClient] = useState<number | null>(null);
  const [filterType, setFilterType] = useState<string>('all');

  useEffect(() => {
    loadBrandData();
  }, []);

  const loadBrandData = async () => {
    try {
      setLoading(true);

      // TODO: Replace with actual API calls when backend is ready
      // For now, use mock data
      const mockGuidelines: BrandGuideline[] = [
        {
          client_id: 1,
          client_name: 'Acme Corporation',
          logo_count: 3,
          color_count: 2,
          font_count: 4,
          guideline_count: 1,
          total_assets: 10,
        },
        {
          client_id: 2,
          client_name: 'TechStart Inc',
          logo_count: 2,
          color_count: 1,
          font_count: 3,
          guideline_count: 1,
          total_assets: 7,
        },
        {
          client_id: 3,
          client_name: 'Global Services LLC',
          logo_count: 1,
          color_count: 1,
          font_count: 2,
          guideline_count: 0,
          total_assets: 4,
        },
      ];

      const mockAssets: BrandAsset[] = [
        {
          id: 1,
          client_id: 1,
          client_name: 'Acme Corporation',
          asset_type: 'logo',
          asset_name: 'Primary Logo - Full Color',
          description: 'Main logo for digital and print',
          created_at: '2024-01-15T10:00:00Z',
        },
        {
          id: 2,
          client_id: 1,
          client_name: 'Acme Corporation',
          asset_type: 'color_palette',
          asset_name: 'Brand Color Palette',
          description: 'Primary and secondary brand colors',
          created_at: '2024-01-15T10:00:00Z',
        },
        {
          id: 3,
          client_id: 2,
          client_name: 'TechStart Inc',
          asset_type: 'logo',
          asset_name: 'Logo - Light Version',
          description: 'Logo for dark backgrounds',
          created_at: '2024-02-01T10:00:00Z',
        },
      ];

      setTimeout(() => {
        setBrandGuidelines(mockGuidelines);
        setAssets(mockAssets);
        setLoading(false);
      }, 500);

    } catch (error) {
      console.error('Failed to load brand data:', error);
      setLoading(false);
    }
  };

  const filteredGuidelines = brandGuidelines.filter(guide =>
    guide.client_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getAssetIcon = (type: string) => {
    switch (type) {
      case 'logo': return ImageIcon;
      case 'color_palette': return Palette;
      case 'typography': return Type;
      case 'brand_guidelines': return FileText;
      default: return FileText;
    }
  };

  const getAssetColor = (type: string) => {
    switch (type) {
      case 'logo': return 'bg-purple-100 text-purple-600';
      case 'color_palette': return 'bg-pink-100 text-pink-600';
      case 'typography': return 'bg-blue-100 text-blue-600';
      case 'brand_guidelines': return 'bg-green-100 text-green-600';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Brand Assets & Guidelines</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage client brand guidelines, logos, colors, and typography
          </p>
        </div>
        <button className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition">
          <Plus className="h-5 w-5 mr-2" />
          Upload Assets
        </button>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center space-x-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by client name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
            />
          </div>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
          >
            <option value="all">All Types</option>
            <option value="logo">Logos</option>
            <option value="color_palette">Color Palettes</option>
            <option value="typography">Typography</option>
            <option value="brand_guidelines">Guidelines</option>
          </select>
        </div>
      </div>

      {/* Brand Guidelines Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {filteredGuidelines.map((guide) => (
          <div
            key={guide.client_id}
            className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow border border-gray-200"
          >
            <div className="p-6">
              {/* Client Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center">
                    <div className="h-12 w-12 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center mr-3">
                      <Palette className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{guide.client_name}</h3>
                      <p className="text-sm text-gray-500">{guide.total_assets} assets</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Asset Type Counts */}
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="bg-purple-50 rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <ImageIcon className="h-5 w-5 text-purple-600" />
                    <span className="text-2xl font-bold text-purple-600">{guide.logo_count}</span>
                  </div>
                  <p className="text-xs text-purple-700 mt-1">Logos</p>
                </div>
                <div className="bg-pink-50 rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <Palette className="h-5 w-5 text-pink-600" />
                    <span className="text-2xl font-bold text-pink-600">{guide.color_count}</span>
                  </div>
                  <p className="text-xs text-pink-700 mt-1">Colors</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <Type className="h-5 w-5 text-blue-600" />
                    <span className="text-2xl font-bold text-blue-600">{guide.font_count}</span>
                  </div>
                  <p className="text-xs text-blue-700 mt-1">Fonts</p>
                </div>
                <div className="bg-green-50 rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <FileText className="h-5 w-5 text-green-600" />
                    <span className="text-2xl font-bold text-green-600">{guide.guideline_count}</span>
                  </div>
                  <p className="text-xs text-green-700 mt-1">Guidelines</p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center space-x-2">
                <button className="flex-1 flex items-center justify-center px-3 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition text-sm font-medium">
                  <Eye className="h-4 w-4 mr-1.5" />
                  View All
                </button>
                <button className="flex items-center justify-center px-3 py-2 bg-gray-50 text-gray-700 rounded-lg hover:bg-gray-100 transition">
                  <Upload className="h-4 w-4" />
                </button>
                <button className="flex items-center justify-center px-3 py-2 bg-gray-50 text-gray-700 rounded-lg hover:bg-gray-100 transition">
                  <Download className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {filteredGuidelines.length === 0 && (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <Palette className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No brand assets found</h3>
          <p className="text-gray-500 mb-6">
            {searchTerm ? 'Try adjusting your search terms' : 'Start by uploading brand guidelines and assets'}
          </p>
          {!searchTerm && (
            <button className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition">
              <Plus className="h-5 w-5 mr-2" />
              Upload Assets
            </button>
          )}
        </div>
      )}
    </div>
  );
}
